<template>
  <h1 class="sr-only">{{ t('support.myTicketsTitle') }}</h1>
  <PageToolbar>
    <IconButton :label="t('support.backToTickets')" variant="ghost" @click="router.push('/support')">
      <LumiIcon name="previous" />
    </IconButton>
    <Badge v-if="ticket" :tone="ticketStatusTone(ticket.status)">{{ t(`support.status.${ticket.status}`) }}</Badge>
    <template v-if="ticket" #actions>
      <!-- Support-staff controls — gated by the caller's capabilities returned
           by the backend (answer / escalate), not by any client-side role
           guess. -->
      <template v-if="showSupporterControls">
        <Field v-if="capabilities.canAnswer" :label="t('support.statusLabel')" class="w-48">
          <Select v-model="statusModel" :options="statusOptions" :disabled="acting" />
        </Field>
        <template v-if="capabilities.canEscalate">
          <Button
            v-if="!ticket.escalatedAt"
            variant="secondary"
            :loading="escalating"
            @click="onEscalate"
          >
            {{ t('support.escalate') }}
          </Button>
          <!-- An escalated ticket can be taken back out of the sysadmin queue
               (#389) — unless a support-access grant is anchored to it, which
               the server refuses with a 409 the hint below explains. -->
          <template v-else>
            <Badge :tone="ticketEscalationTone()" data-test="ticket-escalated">{{ t('support.escalated') }}</Badge>
            <Button
              variant="secondary"
              :loading="deescalating"
              data-test="deescalate"
              @click="onDeescalate"
            >
              {{ t('support.deescalate') }}
            </Button>
          </template>
        </template>
      </template>

      <!-- Owner controls — the requester can close (mark complete) or reopen
           their own ticket (replying also reopens it). Shown only when the
           caller isn't support staff (who has the full status control
           above). -->
      <template v-else-if="isOwner">
        <Button
          v-if="ticket.status !== 'closed'"
          variant="secondary"
          :loading="settingStatus"
          @click="changeStatus('closed')"
        >
          {{ t('support.markComplete') }}
        </Button>
        <Button v-else variant="secondary" :loading="settingStatus" @click="changeStatus('open')">
          {{ t('support.reopen') }}
        </Button>
      </template>
    </template>
  </PageToolbar>

  <SupportLayout>
    <InlineNotice v-if="ticket && deescalateError" variant="error" class="mb-4" data-test="deescalate-error">
      {{ deescalateError }}
    </InlineNotice>

    <div v-if="loading" class="flex justify-center py-20">
      <LumiLoader size="page" :label="t('common.loading')" />
    </div>

    <Card v-else-if="!ticket">
      <EmptyState :title="t('support.empty.notFoundTitle')" :description="t('support.empty.notFoundBody')">
        <template #action>
          <Button variant="secondary" @click="router.push('/support')">{{ t('support.backToTickets') }}</Button>
        </template>
      </EmptyState>
    </Card>

    <template v-else>
      <!-- The ticket subject stays as a content heading (spec §8): the chrome's
           header holds functions, not the page's own title. -->
      <h2 class="text-lg font-semibold text-heading mb-1">{{ ticket.subject }}</h2>
      <p class="text-sm text-tertiary mb-5">{{ subtitleText }}</p>

      <!-- Thread -->
      <div class="flex flex-col gap-3 mb-6">
        <div
          v-for="message in ticket.messages"
          :key="message.id"
          class="rounded-xl border p-4"
          :class="message.authorType === 'user'
            ? 'bg-surface border-default'
            : 'bg-accent-soft border-default'"
        >
          <div class="flex items-center justify-between gap-3 mb-1.5">
            <span class="text-sm font-semibold text-heading">{{ authorLabel(message) }}</span>
            <span class="text-xs text-tertiary whitespace-nowrap">{{ formatDateTime(message.createdAt) }}</span>
          </div>
          <p class="text-sm text-secondary whitespace-pre-wrap leading-relaxed">{{ message.text }}</p>
        </div>
      </div>

      <!-- Reply -->
      <Card padding="md">
        <Field :label="t('support.reply')">
          <Textarea
            v-model="replyText"
            :rows="4"
            :placeholder="t('support.replyPlaceholder')"
            :disabled="acting"
          />
        </Field>
        <div class="flex justify-end mt-3">
          <Button :loading="replying" :disabled="!replyText.trim()" @click="onReply">
            {{ t('support.send') }}
          </Button>
        </div>
      </Card>
    </template>
  </SupportLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Badge, Button, Card, EmptyState, Field, IconButton, LumiIcon, PageToolbar, Select, LumiLoader, Textarea, InlineNotice } from '@lumi/ui';
import type { SelectOption } from '@lumi/ui';
import { useApiMutation } from '@lumi/app-kit';
import SupportLayout from '@/components/SupportLayout.vue';
import { supportApi } from '@/utils/api';
import { useSupportStore, TICKET_STATUSES } from '@/stores/support';
import type { TicketDetail, TicketMessage, TicketStatus } from '@/stores/support';
import { ticketEscalationTone, ticketStatusTone } from '@/utils/ticket-status';
import { useAuthStore } from '@/stores/auth';

const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();
const supportStore = useSupportStore();
const authStore = useAuthStore();

const tenantSlug = computed(() => String(route.params.tenantSlug || ''));
const ticketId = computed(() => String(route.params.id || ''));

const loading = ref(true);
const replyText = ref('');

// The three ticket writes. They are issued here rather than from the store
// (issue 195, D5): each has its own button and its own pending state, and a
// store action cannot own either. All three answer with the full updated
// ticket, which goes back to the store via `adoptTicket` — the shared state
// stays in one place, the request does not.
//
// inlineEdit throughout: the reply appears in the thread, the escalation badge
// flips, the status badge and Select re-read the ticket. Nothing here needs a
// toast to say what the screen just showed. Failures ride the default global
// channel — a supporter toolbar has no in-place slot, and silence on a failed
// escalation would be the worst outcome of the three.
const { pending: replying, mutate: sendReply } = useApiMutation<TicketDetail, [string]>({
  tier: 'inlineEdit',
  run: async (text) => {
    const { data } = await supportApi.post(
      `/tickets/${ticketId.value}/messages`,
      { text },
      supportStore.tenantCfg(tenantSlug.value),
    );
    return supportStore.adoptTicket(data as TicketDetail);
  },
});

const { pending: escalating, mutate: onEscalate } = useApiMutation<TicketDetail, []>({
  tier: 'inlineEdit',
  run: async () => {
    const { data } = await supportApi.post(
      `/tickets/${ticketId.value}/escalate`,
      { reason: undefined },
      supportStore.tenantCfg(tenantSlug.value),
    );
    return supportStore.adoptTicket(data as TicketDetail);
  },
});

// De-escalation (#389) is the one write here whose refusal the supporter has to
// act on rather than retry: a 409 `ticket_has_open_support_access` means a
// consented grant (ADR 0041) is still anchored to the ticket, and the way
// forward is to revoke that grant — so the failure renders IN PLACE, under the
// toolbar, as the specific hint rather than a generic conflict notice.
const {
  pending: deescalating,
  mutate: runDeescalate,
  formError: deescalateFormError,
} = useApiMutation<TicketDetail, []>({
  tier: 'inlineEdit',
  errorChannel: 'inPlace',
  run: async () => {
    const { data } = await supportApi.post(
      `/tickets/${ticketId.value}/deescalate`,
      {},
      supportStore.tenantCfg(tenantSlug.value),
    );
    return supportStore.adoptTicket(data as TicketDetail);
  },
});
const deescalateBlockedByGrant = ref(false);
const deescalateError = computed(() =>
  deescalateBlockedByGrant.value ? t('support.deescalateBlocked') : deescalateFormError.value,
);

async function onDeescalate() {
  deescalateBlockedByGrant.value = false;
  const outcome = await runDeescalate();
  if (!outcome.ok) deescalateBlockedByGrant.value = outcome.code === 'ticket_has_open_support_access';
}

// No rollback needed on failure: `statusModel` is a computed proxy over the
// ticket's own status, so a refused change simply re-reads the unchanged value.
const { pending: settingStatus, mutate: changeStatus } = useApiMutation<TicketDetail, [TicketStatus]>({
  tier: 'inlineEdit',
  run: async (status) => {
    const { data } = await supportApi.patch(
      `/tickets/${ticketId.value}/status`,
      { status },
      supportStore.tenantCfg(tenantSlug.value),
    );
    return supportStore.adoptTicket(data as TicketDetail);
  },
});

const ticket = computed(() => supportStore.current);
const capabilities = computed(() => supportStore.capabilities);
// Show the support-staff card when the caller can answer or escalate. A
// view-only owner/suite_admin has neither and sees no controls (but still the
// thread) — only support_agent works tickets (ADR 0033).
const showSupporterControls = computed(
  () => capabilities.value.canAnswer || capabilities.value.canEscalate,
);
const isOwner = computed(
  () => !!ticket.value && ticket.value.createdByMasterUserId === authStore.user?.userId,
);
const acting = computed(
  () => replying.value || escalating.value || deescalating.value || settingStatus.value,
);

const subtitleText = computed(() =>
  ticket.value ? `${ticket.value.tenantSlug} · ${ticket.value.moduleKey}` : '',
);

const statusOptions = computed<SelectOption[]>(() =>
  TICKET_STATUSES.map((s) => ({ value: s, label: t(`support.status.${s}`) })),
);

// Writable proxy over the live status: setting the Select issues the PATCH and
// the store result becomes the new source of truth.
const statusModel = computed<TicketStatus>({
  get: () => ticket.value?.status ?? 'open',
  set: (value) => {
    if (ticket.value && value !== ticket.value.status) void changeStatus(value);
  },
});

function authorLabel(message: TicketMessage): string {
  if (message.authorEmail) return message.authorEmail;
  if (message.authorType === 'support') return t('support.author.support');
  if (message.authorType === 'system_admin') return t('support.author.systemAdmin');
  return t('support.author.user');
}

function formatDateTime(value: string): string {
  try {
    return new Date(value).toLocaleString(locale.value, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return value;
  }
}

async function load() {
  loading.value = true;
  try {
    await supportStore.loadTicket(tenantSlug.value, ticketId.value);
  } catch {
    // 403/404 already surfaced by the global handler; current stays null and the
    // "not found" state renders.
    supportStore.current = null;
  } finally {
    loading.value = false;
  }
}

async function onReply() {
  const text = replyText.value.trim();
  if (!text) return;
  const outcome = await sendReply(text);
  // Keep what the user typed when the reply did not go through — it is the only
  // copy of it.
  if (outcome.ok) replyText.value = '';
}

// loadTicket populates both the ticket and the caller's capabilities, so the
// supporter controls resolve correctly even on a deep link.
onMounted(() => {
  void load();
});

watch(ticketId, load);
</script>
